import React, {
  useState, Fragment, useEffect, useMemo,
} from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Select, Table } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import Helpers from '@Apis/helpers';
import SelectDepartment from '@Components/Common/SelectDepartment';
import './style.scss';
import { QUANTITY } from '@Apis/constants';
import ReservationActions from '@Actions/reservationActions';
import BatchSelector from '@Components/Inventory/Indent/CreateIndent/BatchSelector';
import FormHelpers from "@Helpers/FormHelpers";
import documentUINamesConstant from '../../../../../apis/documentUINamesConstant';

const ReservationSO = ({ user, selectedSO, createReservation, createReservationLoading, callback }) => {
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [data, setData] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [allStock, setAllStock] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('staticReservations', JSON.stringify({}));
    setSelectedDepartment([selectedSO?.tenant_department_id]);

    return () => {
      sessionStorage.setItem('staticReservations', JSON.stringify({}));
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('staticReservations', JSON.stringify({}));
    const salesOrderLines = selectedSO?.sales_order_lines.map((orderLine) => {
      const selectedDepartmentBatches = FormHelpers.getBatchesByDepartmentIDs(orderLine.tenant_department_wise_product_batches, selectedDepartment)?.filter((item) => !item?.is_reserved) || [];
      const parentKey = uuidv4();
      const batches = FormHelpers.getBatches(
        Helpers.batchSelectionMethod(orderLine.default_outwards_method || Helpers.getBatchMethod(orderLine), selectedDepartmentBatches, (Number(orderLine.quantity) + Number(orderLine.free_quantity))),
        (Number(orderLine.quantity) + Number(orderLine.free_quantity)),
        orderLine.uom_info?.[0],
        orderLine.product_sku_info?.uom_info,
        null,
        parentKey,
      );
      const orderLineQty = ['NON_STORABLE', 'SERVICE'].includes(orderLine?.product_sku_info?.product_type) ? orderLine?.quantity : Math.min(orderLine?.quantity, getAvailableQty(orderLine, batches));

      return {
        ...orderLine,
        key: parentKey,
        quantity: orderLineQty,
        so_quantity: orderLine?.quantity,
        batchConsumptionMethod: orderLine.default_outwards_method || Helpers.getBatchMethod(orderLine),
        product_batches: batches,
        bundle_products: orderLine?.bundle_products?.map((item) => {
          const selectedDepartmentBatches = FormHelpers.getBatchesByDepartmentIDs(item?.tenant_department_wise_product_batches, selectedDepartment)?.filter((item) => !item?.is_reserved) || [];
          const lineKey = uuidv4();
          const bundleBatches = FormHelpers.getBatches(
            Helpers.batchSelectionMethod(item.default_outwards_method || Helpers.getBatchMethod(item), selectedDepartmentBatches, (Number(item.quantity) + Number(item.free_quantity))),
            (Number(item.quantity) + Number(item.free_quantity)),
            item.uom_info?.[0],
            item.product_sku_info?.uom_info,
            null,
            lineKey,
          );
          return {
            ...item,
            parentKey,
            key: lineKey,
            unitPrice: QUANTITY(item?.unit_price) || 0,
            uomId: Number(item?.uom_id),
            uomGroup: Number(item?.group_id),
            uom_info: item?.uom_info,
            product_sku_name: item?.product_sku_info?.product_sku_name,
            so_quantity: item?.quantity,
            expiry_days: item?.product_sku_info?.expiry_days,
            product_sku_info: item?.product_sku_info,
            batchConsumptionMethod: item?.default_outwards_method || Helpers.getBatchMethod(item),
            product_batches: bundleBatches,
          };
        }),
      }
    });
    setData(salesOrderLines)
  }, [selectedDepartment]);

  const getAvailableQty = (record, productBatches) => {
    let availableQty;
    if (record?.bundle_products?.length) {
      const arr = [];
      for (let i = 0; i < record?.bundle_products?.length; i++) {
        const lineKey = record?.bundle_products?.[i]?.key;
        const selectedDepartmentBatches = FormHelpers.getBatchesByDepartmentIDs(record?.bundle_products?.[i]?.tenant_department_wise_product_batches, selectedDepartment)?.filter((item) => !item?.is_reserved) || [];
        const bundleProductBatches = FormHelpers.getBatches(selectedDepartmentBatches, Math.min(record?.bundle_products?.[i]?.quantity, Helpers.getValueTotalInObject(selectedDepartmentBatches, 'available_qty')), record?.bundle_products?.[i]?.uom_info?.[0], record?.bundle_products?.[i]?.product_sku_info?.uom_info, null, lineKey);
        const qty = Helpers.getValueTotalInObject(bundleProductBatches?.filter((item) => item?.batch_in_use), 'available_qty');
        arr.push(qty / (record?.bundle_products[i]?.quantity / record?.quantity));
      }
      availableQty = Math.min(...arr);
    } else {
      availableQty = Helpers.getValueTotalInObject(productBatches?.filter((item) => item?.batch_in_use), 'available_qty');
    }
    return QUANTITY(availableQty, record?.uom_info?.[0]?.precision);
  };

  const handleDelete = (key) => {
    if (data?.length > 1) {
      const copyData = data?.filter((item) => item.key !== key);
      setData(copyData);
    } else {
      notification.open({
        type: 'error',
        message: 'Atleast one order line is required',
        duration: 4,
        placement: 'top',
      });
    }
  };

  const productListColumns = [
    {
      title: 'PRODUCT',
      width: '300px',
      render: (text, record) => {
        return (
          <div>
            <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
              {`#${record?.product_sku_info?.internal_sku_code}`}
            </Link>
            <span>{` - ${record?.product_sku_name}`}</span>
          </div>
        );
      },
    },
    {
      title: 'AVAILABLE STOCK',
      render: (text, record) => {
        let availableQty = 0;
        if (record?.bundle_products?.length) {
          const arr = [];
          for (let i = 0; i < record?.bundle_products?.length; i++) {
            const qty = Helpers.getValueTotalInObject(record?.bundle_products?.[i]?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'available_qty');
            arr.push(qty / ((record?.bundle_products[i]?.so_quantity || 1) / Number(record?.so_quantity || 1)));
          }
          availableQty = Math.min(...arr);
        } else {
          availableQty = Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'available_qty');
        }
        return (
          <div style={{ width: '120px' }}>
            {['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type) && '-'}
            {!['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type) && record?.product_sku_name
              && (
                <Fragment>
                  {availableQty ? `${QUANTITY((availableQty / record?.uom_info?.[0]?.ratio) * record?.product_sku_info?.uom_info?.ratio, (record?.uom_info?.[0]?.precision || 0))} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}` : <span style={{ color: 'red', fontSize: '11px', whiteSpace: 'nowrap' }}>OUT OF STOCK</span>}
                </Fragment>
              )}
            {!['SERVICE', 'BUNDLE'].includes(record?.product_sku_info?.product_type?.toUpperCase()) && availableQty ? (
              <H3Text
                text={`In ${record?.product_batches?.filter((item) => item?.batch_in_use)?.length || '0'} Batches`}
                className="table-subscript"
              />
            ) : ''}
          </div>
        );
      },
    },
    // {
    //   title: 'PENDING',
    //   render: (item) => (
    //     <Fragment>
    //       {`${QUANTITY(item?.issueStockQuantity - item?.invoice_line_adjustment_issued_quantity, item?.uom_info?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`}
    //     </Fragment>
    //   ),
    // },
    {
      title: 'QUANTITY',
      width: '200px',
      render: (record) => {
        const qytValidation = formSubmitted && (Number(record.quantity) <= 0 || Number(record.quantity) > record?.issueStockQuantity - record?.invoice_line_adjustment_issued_quantity);
        const showErrorValidation = Number(record?.quantity) > record?.issueStockQuantity - record?.invoice_line_adjustment_issued_quantity;
        let availableQty = 0;
        if (record?.bundle_products?.length) {
          const arr = [];
          for (let i = 0; i < record?.bundle_products?.length; i++) {
            const qty = Helpers.getValueTotalInObject(record?.bundle_products?.[i]?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'available_qty');
            arr.push(qty / ((record?.bundle_products[i]?.so_quantity || 1) / Number(record?.so_quantity || 1)));
          }
          availableQty = Math.min(...arr);
        } else {
          availableQty = Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'available_qty');
        }
        const valiDationForAvailableQty = record?.bundle_products?.length ? (Number(record?.quantity) > availableQty) : (Number(record?.quantity) > availableQty);

        return (
          <Fragment>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <H3FormInput
                value={record.quantity}
                type="number"
                labelClassName="orgFormLabel"
                inputClassName={`orgFormInput ${qytValidation ? 'orgFormInputError' : ''} `}
                name={valiDationForAvailableQty ? 'quantity less then or equal to available stock' : 'quantity less then or equal to pending quantity'}
                onChange={(event) => handleQuantityChange(record, (event.target.value || 0), 0, availableQty)}
                showError={showErrorValidation || valiDationForAvailableQty}
              />
              <div style={{ marginLeft: '6px' }}>
                {record?.uom_info?.[0]?.uqc?.toProperCase()}
              </div>
            </div>
          </Fragment>
        );
      },
    },
    {
      title: '',
      render: (text, record) => {
        return (
          <Fragment>
            {data.length !== record.key && (
              <div className="update-invoice__delete-line-button" onClick={() => handleDelete(record.key)}>
                <CloseOutlined />
              </div>
            )}
          </Fragment>
        );
      },
      width: '40px',
      fixed: 'right',
    },
  ].filter((item) => !item.hidden);

  const bundleProductListColumns = [
    {
      title: 'PRODUCT',
      render: (text, record) => (
        <div className="invoice-table__product-name">
          <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
            {record?.product_sku_name}
          </Link>
        </div>
      ),
    },
    {
      title: 'AVAILABLE STOCK',
      render: (text, record) => {
        const availableQty = Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => item?.batch_in_use), 'available_qty');
        return (
          <Fragment>
            {['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type) && '-'}
            {!['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type) && record?.product_sku_name
              && (
                <Fragment>
                  {availableQty ? `${QUANTITY((availableQty / record?.uom_info?.[0]?.ratio) * record?.product_sku_info?.uom_info?.ratio, record?.uom_info?.[0]?.precision)} ${record?.uom_info?.[0]?.uqc?.toProperCase()}` : <span style={{ color: 'red', fontSize: '11px', whiteSpace: 'nowrap' }}>OUT OF STOCK</span>}
                </Fragment>
              )}
            {!['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type) && Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => item?.batch_in_use), 'available_qty') ? (
              <H3Text
                text={`In ${record?.product_batches?.filter((item) => item?.batch_in_use)?.length || '0'} Batches`}
                className="table-subscript"
              />
            ) : ''}
          </Fragment>
        );
      },
    },
    {
      title: 'QUANTITY IN BUNDLE',
      render: (text, record) => `${QUANTITY(record?.so_quantity || '0', record?.uom_info?.[0]?.precision)} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}`,
    },
    {
      title: 'INVOICE QUANTITY',
      dataIndex: 'quantity',
      render: (text, record) => (
        <div>{`${Helpers.getValueTotalInObject(record?.product_batches, 'consumed_qty') || 0} ${record?.uom_info?.[0]?.uqc?.toProperCase()}`}</div>
      ),
    },
  ];

  const handleQuantityChange = (record, value, freeValue) => {
    const copyData = JSON.parse(JSON.stringify(data));

    if (record?.bundle_products?.length) {
      const bundleProducts = JSON.parse(JSON.stringify(record?.bundle_products));
      for (let i = 0; i < bundleProducts?.length; i++) {
        bundleProducts[i].product_batches = FormHelpers.getBatches(bundleProducts[i]?.product_batches, (Number(value) + Number(freeValue)) * bundleProducts[i]?.bundle_quantity, bundleProducts[i]?.uom_info?.[0], bundleProducts[i]?.product_sku_info?.uom_info, null, bundleProducts[i]?.key);
        const updatedData = copyData.map((obj) => {
          if (obj.key === record.key) {
            return {
              ...obj,
              quantity: value || '',
              free_quantity: freeValue || '',
              so_free_quantity: freeValue || '',
              bundle_products: bundleProducts,
            };
          }
          return obj;
        });
        setData(updatedData);
      }
    } else {
      const updatedData = copyData.map((obj) => {
        if (obj.key === record.key) {
          return {
            ...obj,
            quantity: value,
            free_quantity: freeValue || '',
            so_free_quantity: freeValue || '',
            product_batches: FormHelpers.getBatches(record?.product_batches, (Number(value || 0) + Number(freeValue || 0)), record?.uom_info?.[0], record?.product_sku_info?.uom_info, null, record?.key),
          };
        }
        return obj;
      });
      setData(updatedData);
    }
  }

  const toggleBatch = (record, adjustmentRow) => {
    const copyData = JSON.parse(JSON.stringify(data));

    for (let i = 0; i < copyData?.length; i++) {
      if (copyData[i]?.key === adjustmentRow?.key) {
        const rowBatches = copyData[i]?.product_batches;
        for (let j = 0; j < rowBatches?.length; j++) {
          if (rowBatches[j]?.batch_id === record?.batch_id) {
            rowBatches[j].batch_in_use = !rowBatches[j]?.batch_in_use;
            copyData[i].quantity -= rowBatches[j].consumed_qty;
            rowBatches[j].consumed_qty = 0;
            FormHelpers.clearLineReservations(copyData[i]?.key);
          }
        }
        break;
      }
    }
    setData(copyData);
  }

  const toggleBatchInner = (record, adjustmentRow) => {
    const copyData = JSON.parse(JSON.stringify(data));
    for (let i = 0; i < copyData?.length; i++) {
      const newData = copyData[i];
      if (newData?.key === adjustmentRow?.parentKey) {
        newData.quantity = 0;
        const bundleProducts = newData?.bundle_products;
        for (let j = 0; j < bundleProducts?.length; j++) {
          const rowBatches = bundleProducts[j]?.product_batches;
          for (let k = 0; k < rowBatches?.length; k++) {
            if (rowBatches[k]?.batch_id === record?.batch_id) {
              rowBatches[k].batch_in_use = !rowBatches[k]?.batch_in_use;
            }
            rowBatches[k].consumed_qty = 0;
            FormHelpers.clearLineReservations(data[i]?.key);
          }
        }
      }
    }
    setData(copyData);
  }

  const isDataValid = () => {
    let isValid = true
    for (let moLine of data) {
      if (moLine?.quantity <= 0) {
        isValid = false;
      }
    }

    return isValid;
  }

  const handleCreateReservation = () => {
    setFormSubmitted(true);
    if (isDataValid()) {

      let result = [];
      data.forEach(orderLine => {
        if (!orderLine?.bundle_products?.length) {
          orderLine.product_batches.forEach(batch => {
            if (batch.consumed_qty > 0) {
              result.push({ ...batch, order_id: orderLine?.order_id, order_line_id: orderLine?.order_line_id });
            }
          });
        } else {
          orderLine?.bundle_products.forEach(bl => {
            bl.product_batches.forEach(batch => {
              if (batch.consumed_qty > 0) {
                result.push({ ...batch, order_id: orderLine?.order_id, order_line_id: orderLine?.order_line_id });
              }
            })
          })
        }
      });

      const payload = result.map((batch) => ({
        batch_id: batch?.batch_id,
        tenant_product_id: batch?.tenant_product_id,
        tenant_id: batch?.tenant_id,
        product_sku_id: batch?.product_sku_id,
        consumed_qty: 0,
        reserved_qty: batch?.consumed_qty,
        reservation_entity_name: "SALES_ORDER",
        reservation_entity_id: batch?.order_id,
        reservation_entity_line_name: "SALES_ORDER_LINE",
        reservation_entity_line_id: batch?.order_line_id,
      }))

      createReservation(payload, () => {
        callback();
      })
    }
  }

  const documentUINames = useMemo(() => documentUINamesConstant(user), [user]);

  return (
    <Fragment>
      <div className="form-reservation__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <SelectDepartment
                title="Department"
                tenentLevelDepartment
                selectedDepartment={selectedDepartment}
                noDropdownAlign
                onChange={(value) => {
                  setSelectedDepartment(value);
                }}
                isMultiple
                emptyNotAllowed
                customStyle={{
                  border: '1px solid rgba(68, 130, 218, 0.2)',
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                  marginBottom: '10px',
                }}
              />
            </div>
          </div>

          {!!selectedDepartment?.length && (
            <div style={{ width: '100%' }}>
              <Table
                title={() => (<div>{`${documentUINames.salesOrderUIName} Lines`}</div>)}
                bordered
                loading={createReservationLoading}
                showHeader
                size="small"
                scroll={{ x: 'max-content' }}
                columns={productListColumns}
                dataSource={data}
                pagination={false}
                expandable={{
                  rowExpandable: (record) => (record?.bundle_products?.length > 0 || (Helpers.getValueTotalInObject(record?.product_batches, 'available_qty')) > 0) && !['SERVICE', 'NON_STORABLE'].includes(record?.product_sku_info?.product_type),
                  expandedRowRender: (record) => (
                    <Fragment>
                      {record.bundle_products?.length ? (
                        <div className="inv__bundle-products__wrapper">
                          <Table
                            bordered
                            loading={createReservationLoading}
                            showHeader
                            size="small"
                            columns={bundleProductListColumns}
                            dataSource={record?.bundle_products}
                            pagination={false}
                            expandable={{
                              defaultExpandAllRows: false,
                              rowExpandable: (innerRecord) => innerRecord?.product_sku_name && Helpers.getValueTotalInObject(innerRecord?.product_batches, 'available_qty'),
                              expandedRowRender: (innerRecord) => (
                                <BatchSelector
                                  innerBatchSelector
                                  batchMethod={innerRecord?.batchConsumptionMethod}
                                  updateBatchConsumptionMethod={(value) => {
                                    const copyData = JSON.parse(JSON.stringify(data));
                                    const sortedData = copyData?.map((item) => {
                                      if (innerRecord.key === item.key) {
                                        item.batchConsumptionMethod = value;
                                        item.product_batches = Helpers.batchSelectionMethod(value, item.product_batches, item.quantity, allStock, null, item?.uom_info);
                                      }
                                      return {
                                        ...item,
                                      };
                                    });
                                    setData(sortedData);
                                  }}
                                  batches={innerRecord?.product_batches}
                                  uomInfo={innerRecord?.uom_info?.[0]}
                                  productUomInfo={innerRecord?.product_sku_info?.uom_info}
                                  expiryDays={innerRecord?.product_sku_info?.expiry_days}
                                  itemsRow={innerRecord}
                                  onToggleBatch={(batch, adjustmentRow) => toggleBatchInner(batch, adjustmentRow)}
                                />
                              ),
                            }}
                            scroll={{ x: 'max-content' }}
                          />
                        </div>
                      ) : (
                        <React.Fragment>
                          <BatchSelector
                            allStock={allStock}
                            upDateAllStock={() => {
                              const copyData = JSON.parse(JSON.stringify(data));
                              const filteredData = copyData?.map((item) => {
                                if (record?.key === item?.key) {
                                  for (let i = 0; i < item?.product_batches?.length; i++) {
                                    item.product_batches[i].consumed_qty = 0;
                                  }
                                  item.quantity = 0;
                                }
                                return {
                                  ...item,
                                };
                              });
                              setData(filteredData);
                              setAllStock(!allStock);
                            }}
                            batchMethod={record?.batchConsumptionMethod}
                            updateBatchConsumptionMethod={(value) => {
                              const copyData = JSON.parse(JSON.stringify(data));
                              const sortedData = copyData?.map((item) => {
                                if (record.key === item.key) {
                                  item.batchConsumptionMethod = value;
                                  item.product_batches = Helpers.batchSelectionMethod(value, item.product_batches, item.quantity, allStock, null, item?.uom_info);
                                  item.free_quantity = 0;
                                }
                                return {
                                  ...item,
                                };
                              });
                              setData(sortedData);
                            }}
                            batches={record?.product_batches || []}
                            uomInfo={record?.uom_info?.[0]}
                            productUomInfo={record?.product_sku_info?.uom_info}
                            expiryDays={record?.product_sku_info?.expiry_days}
                            itemsRow={record}
                            onToggleBatch={(batch, adjustmentRow) => toggleBatch(batch, adjustmentRow)}
                          />

                        </React.Fragment>
                      )}
                    </Fragment>
                  ),
                }}
              />
            </div>
          )}
        </div>

        <div className="custom-drawer__footer" style={{ display: 'flex', width: '955px', paddingTop: '10px' }}>
          <H3Button
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            text="Issue Stock"
            onClick={() => {
              handleCreateReservation();
            }}
            style={{
              width: '180px',
              borderRadius: '5px',
              padding: '7px 0px',
              marginLeft: '0',
            }}
            isLoading={createReservationLoading}
          />
        </div>
      </div>
    </Fragment>
  )
}

const mapStateToProps = ({
  UserReducers, ReservationReducers,
}) => ({
  user: UserReducers.user,
  createReservationLoading: ReservationReducers.createReservationLoading,
  updateFreeReservationLoading: ReservationReducers.updateFreeReservationLoading,
});

const mapDispatchToProps = (dispatch) => ({
  createReservation: (payload, callback) => dispatch(ReservationActions.createReservation(payload, callback)),
  updateFreeReservation: (payload, callback) => dispatch(ReservationActions.updateFreeReservation(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ReservationSO));